/** Pagina statistiche di un flusso — condivisa, non duplicata per verticale.
 *
 * Una sola implementazione per RDI e DDT (e per i verticali che seguiranno):
 * `BscStats` e `RinascenteStats` sono cloni strutturali l'uno dell'altro, e
 * aggiungerne un terzo avrebbe garantito che i tre divergessero.
 *
 * Ordine verticale, deciso col cliente:
 *   1. **il valore**: quanto Sieve ha gestito da solo e bene, detto in una frase
 *      invece che come tasso da interpretare, col rovescio (quanto ha sbagliato)
 *      subito sotto e il suo andamento
 *   2. volume e tempo di ciclo
 *   3. adesso — arretrato per età, pratiche piantate, coda non prelevata
 *   4. scarti — dove si capisce *quanto* e *come* il sistema sbaglia
 *   5. campi corretti a mano
 *
 * Non c'è un riquadro "se accendessimo l'invio automatico": il suo numero è il
 * valore in cima, e teneva in fondo la risposta alla domanda principale. Della
 * soglia di confidenza qui non si parla — non serve a decidere nulla e sposta
 * l'attenzione dal numero che conta (il fatto che sia una costante è registrato
 * in AGENTS.md, dove serve).
 *
 * Regole di visualizzazione seguite (skill dataviz):
 *   * **nessun doppio asse**: entrate/approvate e tasso senza-intervento sono due
 *     grafici, non due scale nello stesso riquadro;
 *   * le due serie hanno legenda **ed** etichette dirette, perché la separazione
 *     tritan fra il blu e il verde di Indaco è 6,7 (banda 6–8, ammessa solo con
 *     codifica secondaria);
 *   * le fasce d'età non usano il colore: la lunghezza porta il valore e
 *     l'etichetta l'identità, quindi un tono unico;
 *   * `--indaco-danger` compare solo come colore di **stato** (la famiglia
 *     "il sistema ha sbagliato"), sempre con icona ed etichetta;
 *   * il design system è light-only, quindi non ci sono varianti scure
 *     speculative.
 *
 * Un blocco con `available: false` mostra il proprio motivo al posto del numero:
 * uno zero si leggerebbe come "nessun caso" invece di "metrica inesistente".
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { fetchStatsOverview } from "../api/client";
import type {
  StatsAvailability,
  StatsComparison,
  StatsCounter,
  StatsDrill,
  StatsEntityDimension,
  StatsFlow,
  StatsOverview,
  StatsPeriod,
} from "../api/types";
import { statusMeta } from "../lib/status";
import { drillHref } from "../lib/statsRoutes";

/** Come si chiamano gli oggetti del flusso quando si parla al cliente. */
const NOME_OGGETTI: Record<string, string> = {
  rdi: "ticket",
  ddt: "documenti",
};

const PERIODS: { days: number | "start"; label: string }[] = [
  { days: 1, label: "Oggi" },
  { days: 7, label: "7 giorni" },
  { days: 30, label: "30 giorni" },
  { days: 90, label: "90 giorni" },
  // "start" ⇒ il client manda `since=start` invece di `days` (vedi client.ts).
  // La finestra parte dalla prima pratica del flusso, non da un N calcolato
  // qui: un `days` scorrevole mentirebbe fra un anno (piano D1).
  { days: "start", label: "Dall'inizio" },
];

/** Serie categoriali: coppia validata contro la superficie chiara (ΔE normale 24,5). */
const SERIE_INGRESSO = "var(--indaco-brand-primary)";
const SERIE_APPROVATE = "var(--indaco-success)";
const ASSE = "var(--indaco-gray-600)";
const GRIGLIA = "var(--indaco-gray-200)";

// ── formattazione ────────────────────────────────────────────────────────────

const nf = new Intl.NumberFormat("it-IT");

function pct(value?: number | null): string {
  return value === null || value === undefined ? "—" : `${value.toFixed(1).replace(".", ",")}%`;
}

/** Durata leggibile: minuti sotto l'ora, ore sotto il giorno, poi giorni. */
function durata(seconds?: number | null): string {
  if (seconds === null || seconds === undefined) return "—";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  const ore = seconds / 3600;
  if (ore < 48) return `${ore.toFixed(1).replace(".", ",")} h`;
  return `${(ore / 24).toFixed(1).replace(".", ",")} g`;
}

function dataBreve(iso: string): string {
  const [, m, d] = iso.split("-");
  return `${d}/${m}`;
}

/** GG/MM/AAAA a partire dai primi 10 caratteri dell'ISO ("YYYY-MM-DD..."),
 *  senza passare da `Date`: `period.start`/`first_at` sono già la mezzanotte
 *  locale del fuso del tenant, e convertirli con `Intl` rischierebbe di
 *  spostarli di un giorno. */
function formattaDataItaliana(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
}

/** Frase dell'intestazione: dichiara da quando la pagina sta contando.
 *
 * Con "Dall'inizio" (`is_full_history`) usa `first_at`; altrimenti (compreso
 * un backend vecchio che non manda ancora i campi nuovi) stampa `dal … al …`
 * da `start`/`end`, che esistono sempre. `is_full_history` può essere vero
 * con `first_at` nullo sul flusso vuoto di un tenant nuovo (piano D1): anche
 * lì si ripiega su `dal … al …`.
 */
function descrizionePeriodo(period: StatsPeriod): string {
  const giorni = `${nf.format(period.days)} giorni`;
  if (period.is_full_history && period.first_at) {
    return `dalla prima pratica del ${formattaDataItaliana(period.first_at)} a oggi · ${giorni}`;
  }
  return `dal ${formattaDataItaliana(period.start)} al ${formattaDataItaliana(period.end)} · ${giorni}`;
}

function quandoRelativo(iso?: string | null): string {
  if (!iso) return "—";
  const giorni = (Date.now() - new Date(iso).getTime()) / 86_400_000;
  if (giorni < 1) return "oggi";
  if (giorni < 2) return "ieri";
  return `${Math.floor(giorni)} giorni fa`;
}

// ── pezzi riusabili ──────────────────────────────────────────────────────────

/** Motivo per cui un blocco è vuoto, al posto di uno zero fuorviante. */
function NonDisponibile({ availability }: { availability: StatsAvailability }) {
  return (
    <p className="text-muted text-sm" style={{ margin: 0, lineHeight: 1.5 }}>
      <span
        className="material-symbols-outlined"
        style={{ fontSize: 16, verticalAlign: "-3px", marginRight: "var(--space-1)" }}
      >
        info
      </span>
      {availability.reason || "Metrica non disponibile per questo flusso."}
    </p>
  );
}

/** Variazione rispetto al periodo precedente. `null` quando non è definita. */
function Delta({ comparison }: { comparison: StatsComparison }) {
  const d = comparison.delta_pct;
  if (d === null || d === undefined) {
    return (
      <span className="text-muted text-xs" title="Nel periodo precedente non c'erano pratiche: la variazione non è definita">
        — nessun confronto
      </span>
    );
  }
  const su = d >= 0;
  return (
    <span
      className="text-xs"
      style={{ color: su ? "var(--indaco-success)" : "var(--indaco-danger)", fontWeight: 600 }}
      title={`Periodo precedente: ${nf.format(comparison.previous)}`}
    >
      <span className="material-symbols-outlined" style={{ fontSize: 14, verticalAlign: "-2px" }}>
        {su ? "trending_up" : "trending_down"}
      </span>
      {su ? "+" : ""}
      {d.toFixed(1).replace(".", ",")}%
    </span>
  );
}

function Riquadro({
  titolo,
  nota,
  azione,
  children,
}: {
  titolo: string;
  nota?: string;
  azione?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="card" style={{ padding: "var(--space-4)" }}>
      <header
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: "var(--space-2)",
          marginBottom: "var(--space-3)",
          flexWrap: "wrap",
        }}
      >
        <h2
          className="text-sm"
          style={{ margin: 0, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}
        >
          {titolo}
        </h2>
        {nota && (
          <span className="text-muted text-xs" style={{ minWidth: 0 }}>
            {nota}
          </span>
        )}
        {azione && <div style={{ marginLeft: "auto" }}>{azione}</div>}
      </header>
      {children}
    </section>
  );
}

/** Numero, con il link alla lista che lo riproduce quando esiste. */
function Numero({
  valore,
  drill,
  titolo,
}: {
  valore: number | string;
  drill?: StatsDrill | null;
  titolo?: string;
}) {
  const testo = typeof valore === "number" ? nf.format(valore) : valore;
  if (!drill) return <span title={titolo}>{testo}</span>;
  return (
    <Link to={drillHref(drill)} title={titolo ?? "Apri le pratiche che compongono questo numero"}>
      {testo}
    </Link>
  );
}

/** Classifica a barre orizzontali: una serie, quindi un tono unico e nessuna legenda.
 *
 * `evidenzia` marca le voci che sono un fallimento del sistema — colore di stato,
 * mai da solo: porta icona ed etichetta.
 */
function Classifica({
  voci,
  totale,
  evidenzia,
}: {
  voci: Array<{ key: string; label: string; value: number; drill?: StatsDrill | null }>;
  totale?: number;
  evidenzia?: (key: string) => boolean;
}) {
  const max = Math.max(1, ...voci.map((v) => v.value));
  if (!voci.length) return <p className="text-muted text-sm" style={{ margin: 0 }}>Nessun dato nel periodo.</p>;
  return (
    <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "var(--space-2)" }}>
      {voci.map((v) => {
        const critico = evidenzia?.(v.key) ?? false;
        const contenuto = (
          <>
            <div style={{ minWidth: 0 }}>
              <div
                className="text-xs truncate"
                style={{ display: "flex", alignItems: "center", gap: "var(--space-1)" }}
                title={v.label}
              >
                {critico && (
                  <span
                    className="material-symbols-outlined"
                    style={{ fontSize: 14, color: "var(--indaco-danger)", flexShrink: 0 }}
                    aria-hidden
                  >
                    error
                  </span>
                )}
                <span className="truncate">{v.label}</span>
                {critico && (
                  <span className="badge badge-danger badge-sm" style={{ flexShrink: 0 }}>
                    fallimento del sistema
                  </span>
                )}
              </div>
              {/* Barra sottile, estremità arrotondata, ancorata alla base. */}
              <div
                style={{
                  height: 6,
                  marginTop: 2,
                  borderRadius: "var(--border-radius-sm)",
                  background: "var(--indaco-gray-100)",
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    width: `${(v.value / max) * 100}%`,
                    height: "100%",
                    borderRadius: "var(--border-radius-sm)",
                    background: critico ? "var(--indaco-danger)" : SERIE_INGRESSO,
                  }}
                />
              </div>
            </div>
            <div className="text-xs" style={{ textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 }}>
              {nf.format(v.value)}
              {totale ? <span className="text-muted"> · {pct((v.value / totale) * 100)}</span> : null}
            </div>
          </>
        );

        const griglia: React.CSSProperties = {
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: "var(--space-2)",
          alignItems: "center",
        };

        // **Tutta la riga** è il link, non solo il numero: un bersaglio di quattro
        // caratteri sul bordo destro non si trova, e chi guarda clicca il gruppo.
        // Segnalato guardando la pagina il 2026-07-29 ("ma non vedo il filtro").
        return (
          <li key={v.key}>
            {v.drill ? (
              <Link
                to={drillHref(v.drill)}
                className="stats-rank-row"
                style={{ ...griglia, color: "inherit", textDecoration: "none" }}
                title={`Apri le ${nf.format(v.value)} pratiche di «${v.label}»`}
              >
                {contenuto}
              </Link>
            ) : (
              <div style={griglia}>{contenuto}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Ripartizione per entità: quanto ricorre una voce, e quanto è costata a mano.
 *
 * Due numeri per riga e una sola barra: la barra porta la frequenza, la quota di
 * corrette resta un numero. Codificare anche la quota con un colore o una seconda
 * barra metterebbe due scale nella stessa riga, che è la regola che questa pagina
 * evita già nei grafici.
 *
 * La classifica è troncata, quindi dichiara cosa non mostra: dieci righe senza
 * quella nota si leggono come l'elenco completo.
 */
function RipartizioneEntita({ dim }: { dim: StatsEntityDimension }) {
  if (!dim.available) return <NonDisponibile availability={dim} />;
  if (!dim.items.length) {
    return <p className="text-muted text-sm" style={{ margin: 0 }}>Nessun dato nel periodo.</p>;
  }
  const max = Math.max(1, ...dim.items.map((i) => i.practices));
  const nascoste = dim.distinct_values - dim.items.length;
  return (
    <>
      <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "var(--space-2)" }}>
        {dim.items.map((i) => {
          const contenuto = (
            <>
              <div style={{ minWidth: 0 }}>
                <div className="text-xs truncate" title={i.label}>
                  {i.label}
                </div>
                <div
                  style={{
                    height: 6,
                    marginTop: 2,
                    borderRadius: "var(--border-radius-sm)",
                    background: "var(--indaco-gray-100)",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${(i.practices / max) * 100}%`,
                      height: "100%",
                      borderRadius: "var(--border-radius-sm)",
                      background: SERIE_INGRESSO,
                    }}
                  />
                </div>
              </div>
              <div className="text-xs" style={{ textAlign: "right", whiteSpace: "nowrap", fontWeight: 600 }}>
                {nf.format(i.practices)}
              </div>
              <div
                className="text-xs"
                style={{ textAlign: "right", whiteSpace: "nowrap", minWidth: 78 }}
                title={`${nf.format(i.corrected)} pratiche di «${i.label}» hanno avuto almeno una correzione manuale`}
              >
                {i.corrected === 0 ? (
                  <span className="text-muted">—</span>
                ) : (
                  <>
                    {nf.format(i.corrected)}
                    <span className="text-muted"> · {pct(i.corrected_pct)}</span>
                  </>
                )}
              </div>
            </>
          );
          const griglia: React.CSSProperties = {
            display: "grid",
            gridTemplateColumns: "1fr auto auto",
            gap: "var(--space-2)",
            alignItems: "center",
          };
          return (
            <li key={i.key}>
              {i.drill ? (
                <Link
                  to={drillHref(i.drill)}
                  className="stats-rank-row"
                  style={{ ...griglia, color: "inherit", textDecoration: "none" }}
                  title={`Apri le ${nf.format(i.practices)} pratiche di «${i.label}»`}
                >
                  {contenuto}
                </Link>
              ) : (
                <div style={griglia}>{contenuto}</div>
              )}
            </li>
          );
        })}
      </ul>
      {nascoste > 0 && (
        <p className="text-muted text-xs" style={{ margin: "var(--space-2) 0 0" }}>
          Mostrate le {dim.items.length} voci più frequenti su {nf.format(dim.distinct_values)}; le
          altre {nf.format(nascoste)} raccolgono {nf.format(dim.other_practices)} pratiche.
        </p>
      )}
    </>
  );
}

/** Tooltip dei grafici, con i token del design system. */
function TooltipGrafico({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="card"
      style={{
        padding: "var(--space-2)",
        fontSize: "var(--font-size-xs)",
        boxShadow: "var(--shadow-md)",
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 2 }}>{label}</div>
      {payload.map((p: any) => (
        <div key={p.name} style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 2,
              background: p.color,
              flexShrink: 0,
            }}
            aria-hidden
          />
          <span className="text-muted">{p.name}</span>
          <span style={{ marginLeft: "auto", fontWeight: 600 }}>
            {p.dataKey === "untouched_rate" ? pct(p.value) : nf.format(p.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── pagina ───────────────────────────────────────────────────────────────────

export default function StatsFlowPage({ flow }: { flow: StatsFlow }) {
  const [days, setDays] = useState<number | "start">(30);
  const [segment, setSegment] = useState<string | null>(null);
  // La dimensione sta nell'URL, non nello stato locale: un link condiviso deve
  // riaprire la stessa lettura del riquadro, non quella di default.
  const [searchParams, setSearchParams] = useSearchParams();
  const dimension = searchParams.get("dimension") || "";
  const [data, setData] = useState<StatsOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState(true);

  const setDimension = useCallback(
    (value: string) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          // `overall` è il default del server: non sporcare l'URL per dirlo.
          if (!value || value === "overall") next.delete("dimension");
          else next.set("dimension", value);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const load = useCallback(() => {
    setLoading(true);
    fetchStatsOverview(flow, days, undefined, segment ?? undefined, dimension || undefined)
      .then((d) => {
        setData(d);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : "Errore caricamento statistiche"))
      .finally(() => setLoading(false));
  }, [flow, days, segment, dimension]);

  useEffect(load, [load]);

  const serie = useMemo(
    () =>
      (data?.daily ?? []).map((p) => ({
        ...p,
        etichetta: dataBreve(p.date),
      })),
    [data],
  );

  if (isLoading && !data) {
    return (
      <div style={{ padding: "var(--space-6)" }}>
        <div className="spinner" />
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: "var(--space-6)" }}>
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }
  if (!data) return null;

  const { automation, volume, cycle_time, backlog, discards, projection, hotspots } = data;
  const nomeOggetti = NOME_OGGETTI[data.flow] ?? "pratiche";
  const tierAuto = automation.tiers.find((t) => t.key === "automatic");
  const tierConfermate = automation.tiers.find((t) => t.key === "confirmed");
  const tierCorrette = automation.tiers.find((t) => t.key === "corrected");
  // Su quale dimensione il riquadro conta le correzioni. Vuoto = il flusso non
  // le dichiara (DDT) oppure il server non è ancora aggiornato: in entrambi i
  // casi la select non esiste e la pagina resta identica a prima.
  const dimensioni = automation.dimensions ?? [];
  const dimensioneAttiva = automation.dimension ?? (dimension || "overall");
  const opzioneAttiva = dimensioni.find((o) => o.key === dimensioneAttiva);
  // Il testo di aiuto serve perché l'etichetta da sola mente: "Motivo" non dice
  // che una riscrittura redazionale non è per forza un errore.
  const selettoreDimensione =
    dimensioni.length > 0 ? (
      <div style={{ display: "grid", gap: "var(--space-1)", justifyItems: "end", minWidth: 0 }}>
        <label className="sr-only" htmlFor="stats-dimension">
          Dimensione su cui contare le correzioni
        </label>
        <select
          id="stats-dimension"
          className="form-select"
          style={{ width: "auto", minWidth: 220, fontSize: "var(--font-size-sm)" }}
          value={dimensioneAttiva}
          title={opzioneAttiva?.description}
          onChange={(e) => setDimension(e.target.value)}
        >
          {dimensioni.map((o) => (
            <option key={o.key} value={o.key} title={o.description}>
              {o.label}
            </option>
          ))}
        </select>
        {opzioneAttiva?.description && (
          <span
            className="text-muted text-xs"
            style={{ maxWidth: 320, textAlign: "right", lineHeight: 1.4 }}
          >
            {opzioneAttiva.description}
          </span>
        )}
      </div>
    ) : undefined;
  // Numeratore di `untouched_rate`, com'è definito nel contratto: i livelli senza
  // intervento umano sono due, non uno. Tenerli insieme qui è ciò che impedisce
  // alla frase di spiegare un numero diverso da quello mostrato.
  const intatte = automation.tiers
    .filter((t) => t.key === "automatic" || t.key === "confirmed")
    .reduce((somma, t) => somma + t.count, 0);
  // Il riquadro Scarti ha qualcosa da dire se c'è almeno uno scarto, oppure se una
  // ripartizione è calcolabile (e allora vale la pena mostrare che è vuota).
  const scartiDaMostrare =
    discards.total.current > 0 ||
    discards.families_availability.available ||
    discards.by_operator_availability.available ||
    discards.automatic_availability.available;

  return (
    <div style={{ display: "grid", gap: "var(--space-4)", minWidth: 0 }}>
      {/* ── intestazione: periodo e segmento, in una riga sopra i grafici ── */}
      <header style={{ display: "flex", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap" }}>
        <h1 className="text-lg" style={{ margin: 0, fontWeight: 700, minWidth: 0 }}>
          Statistiche · {data.flow_label}
        </h1>
        <div style={{ display: "flex", gap: "var(--space-1)", marginLeft: "auto", flexWrap: "wrap" }}>
          {PERIODS.map((p) => (
            <button
              key={p.days}
              type="button"
              className={days === p.days ? "btn btn-primary btn-sm" : "btn btn-outline btn-sm"}
              onClick={() => setDays(p.days)}
            >
              {p.label}
            </button>
          ))}
        </div>
      </header>

      {/* Da quando la pagina sta contando: la data non è mai stata visibile
          prima, e "Dall'inizio" senza un intervallo stampato si leggerebbe
          come un bottone che non fa nulla. */}
      <p className="text-muted text-sm" style={{ margin: 0 }}>
        {descrizionePeriodo(data.period)}
      </p>
      {data.period.has_previous === false && (
        <p className="text-muted text-xs" style={{ margin: 0 }}>
          Nessun periodo precedente da confrontare: la finestra parte dalla prima pratica.
        </p>
      )}

      {/* Selettore di segmento coi suoi pesi: la scelta non è cieca. Due numeri
          per voce — entrate (quanto volume) e aperte (se c'è da fare) — perché
          su Casadei è la coppia a spiegare gli allarmi. */}
      {data.segments.length > 0 && (
        <div style={{ display: "flex", gap: "var(--space-1)", flexWrap: "wrap", alignItems: "center" }}>
          {data.segments.map((s) => (
            <button
              key={s.key}
              type="button"
              className={data.segment === s.key ? "btn btn-primary btn-sm" : "btn btn-outline btn-sm"}
              onClick={() => setSegment(s.key)}
              title={`${nf.format(s.ingested)} entrate nel periodo · ${nf.format(s.open_now)} aperte adesso`}
            >
              {s.label}
              <span className="text-xs" style={{ opacity: 0.8, marginLeft: "var(--space-1)" }}>
                {nf.format(s.ingested)}
                {s.open_now > 0 ? ` · ${nf.format(s.open_now)} da fare` : ""}
              </span>
            </button>
          ))}
          <button
            type="button"
            className={data.segment === null ? "btn btn-primary btn-sm" : "btn btn-outline btn-sm"}
            onClick={() => setSegment("all")}
          >
            Tutti
          </button>
        </div>
      )}

      {/* Cosa la pagina non sta guardando. Una riga, non un riquadro: non è una
          metrica, è la spiegazione di un totale che altrimenti non torna. Sul DDT
          sono 92 email arrivate senza allegato PDF su 194 — senza questa riga
          nessuno saprebbe perché i documenti sono 102. */}
      {data.excluded && data.excluded.excluded > 0 && (
        <p className="text-muted text-xs" style={{ margin: 0, lineHeight: 1.5 }} title={data.excluded.reason}>
          <span
            className="material-symbols-outlined"
            style={{ fontSize: 14, verticalAlign: "-2px", marginRight: "var(--space-1)" }}
            aria-hidden
          >
            filter_alt_off
          </span>
          {nf.format(data.excluded.excluded)} {data.excluded.label.toLowerCase()}: fuori da tutti i
          conteggi di questa pagina.
        </p>
      )}

      {/* ── 1. Il valore, per primo e in chiaro ──
          Non un tasso da interpretare ma la frase che risponde alla domanda:
          quanto il sistema ha gestito da solo, e bene. Il dettaglio viene dopo.
          Niente riferimenti alla soglia di confidenza: qui non serve a decidere
          nulla e sposta l'attenzione dal numero che conta. */}
      <Riquadro titolo={`Sieve ha gestito da solo`} azione={selettoreDimensione}>
        {!automation.available ? (
          <NonDisponibile availability={automation} />
        ) : (
          <div style={{ display: "grid", gap: "var(--space-3)" }}>
            <div style={{ display: "flex", alignItems: "flex-end", gap: "var(--space-4)", flexWrap: "wrap" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: "3rem", fontWeight: 700, lineHeight: 1 }}>
                  {pct(automation.untouched_rate)}
                </div>
                {/* "approvate" e non "documenti": il denominatore di questo tasso
                    sono le pratiche approvate, non quelle arrivate. Sui flussi dove
                    quasi tutto viene approvato le due letture coincidono, sul DDT
                    no — 15 approvate su 102 arrivate — e la frase prometteva un
                    numero sette volte più grande di quello vero. */}
                <div style={{ fontSize: "var(--font-size-base)", marginTop: "var(--space-1)" }}>
                  {/* La chiusa della frase cambia con la dimensione, quindi arriva
                      dal server — **verbo incluso**. Tenere "è stato" qui fuori
                      produceva "è stato con le entità identificate correttamente",
                      sgrammaticato su ogni lente tranne quella storica (visto in
                      produzione il 2026-09-08). Il fallback è la frase di prima,
                      così un server non aggiornato non fa sparire nulla. */}
                  dei {nomeOggetti} <strong>approvati</strong>{" "}
                  {automation.untouched_label ? (
                    <strong>{automation.untouched_label}</strong>
                  ) : (
                    <>
                      è stato gestito <strong>in autonomia e bene</strong>
                    </>
                  )}
                </div>
                <div className="text-muted text-xs">
                  {/* Somma dei livelli senza intervento, non il solo "confermate":
                      `untouched_rate` in cima include anche le auto-approvate, e
                      prendere un solo livello faceva divergere il numero dalla
                      percentuale che dovrebbe spiegare. Oggi nessun flusso ha
                      auto-approvazione attiva, quindi i due valori coincidono: è un
                      disallineamento latente, non ancora visibile. */}
                  {nf.format(intatte)} su {nf.format(automation.approved_total.current)} approvate,
                  senza che l'operatore cambiasse nulla
                  {volume.ingested.current > 0 && (
                    <>
                      {" · "}
                      {pct((intatte / volume.ingested.current) * 100)} di tutte le{" "}
                      {nf.format(volume.ingested.current)} arrivate nel periodo
                    </>
                  )}
                </div>
              </div>
              <div style={{ marginLeft: "auto" }}>
                <Delta comparison={automation.approved_total} />
              </div>
            </div>

            <div style={{ display: "grid", gap: "var(--space-2)", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
              {[tierAuto, tierConfermate, tierCorrette].map((t) =>
                t ? (
                  <div key={t.key} style={{ minWidth: 0 }}>
                    <div className="text-xs text-muted truncate">{t.label}</div>
                    {t.availability.available ? (
                      <div style={{ fontWeight: 700 }}>
                        <Numero valore={t.count} drill={t.drill} />
                        <span className="text-muted text-xs"> · {pct(t.pct)}</span>
                      </div>
                    ) : (
                      <div
                        className="text-muted text-sm"
                        title={t.availability.reason ?? undefined}
                        style={{ display: "flex", alignItems: "center", gap: "var(--space-1)" }}
                      >
                        <span className="badge badge-neutral badge-sm">non attivo</span>
                      </div>
                    )}
                  </div>
                ) : null,
              )}
            </div>

            {/* Qui NON si dice quante sarebbero partite sbagliate se l'invio
                fosse automatico. Sarebbe un controfattuale che poggia su un
                artefatto — oggi la confidenza e' piatta a 0,90, quindi "non le
                avremmo distinte" descrive lo stato del punteggio, non quanto il
                sistema sbagli. Il 92,6% sopra invece e' osservato: l'operatore ha
                approvato senza cambiare nulla.
                I casi in cui il sistema ha sbagliato restano un fatto misurato e
                stanno nel riquadro Scarti, dove il tema e' quello. Una misura di
                accuratezza vera arrivera' con la ground truth
                (docs/GROUND-TRUTH-CASADEI-RDI-ANALYSIS.md, branch
                feat/ground-truth-casadei): solo allora si potra' dire quanto
                l'automazione sbaglierebbe. */}

            {/* Andamento del tasso: grafico a sé, non una seconda scala nel
                riquadro del volume. I giorni senza approvazioni hanno tasso
                nullo e restano un buco: uno zero disegnerebbe un crollo dove
                semplicemente non si è lavorato. */}
            <div style={{ height: 132 }}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={serie} margin={{ top: 4, right: 8, bottom: 0, left: -12 }}>
                  <CartesianGrid stroke={GRIGLIA} vertical={false} />
                  <XAxis
                    dataKey="etichetta"
                    tick={{ fontSize: 10, fill: ASSE }}
                    stroke={GRIGLIA}
                    interval="preserveStartEnd"
                    minTickGap={24}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: ASSE }}
                    stroke={GRIGLIA}
                    domain={[0, 100]}
                    unit="%"
                    // 48 e non 40: a 40 l'etichetta "100%" veniva tagliata in "00%".
                    width={48}
                  />
                  <Tooltip content={<TooltipGrafico />} />
                  <Line
                    type="monotone"
                    dataKey="untouched_rate"
                    name="Senza intervento"
                    stroke={SERIE_INGRESSO}
                    strokeWidth={2}
                    // Punti visibili, non solo la linea: un giorno con un tasso fra
                    // due giorni senza approvazioni non ha vicini da collegare, e
                    // con dot={false} sparirebbe del tutto.
                    dot={{ r: 2 }}
                    activeDot={{ r: 4 }}
                    connectNulls={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </Riquadro>

      {/* D4: la riga sopra dice "approvati" e "arrivate nel periodo" — due
          date diverse (approved_at vs created_at) che la frase non
          distingueva. Qui si dichiara esplicitamente, perché su "Dall'inizio"
          la divergenza smette di essere latente (732 entrate contro 415
          approvate su Casadei RDI). */}
      {automation.available && (
        <p className="text-muted text-xs" style={{ margin: 0 }}>
          Le approvate sono quelle <strong>approvate</strong> nel periodo, anche se arrivate prima;
          le entrate sono contate sulla data di arrivo.
        </p>
      )}

      {/* ── 2. volume e tempo di ciclo ── */}
      <div style={{ display: "grid", gap: "var(--space-4)", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))" }}>
        <Riquadro titolo="Volume" nota={`${nf.format(volume.ingested.current)} entrate`} azione={<Delta comparison={volume.ingested} />}>
          <div style={{ height: 148 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={serie} margin={{ top: 4, right: 8, bottom: 0, left: -12 }} barGap={2}>
                <CartesianGrid stroke={GRIGLIA} vertical={false} />
                <XAxis
                  dataKey="etichetta"
                  tick={{ fontSize: 10, fill: ASSE }}
                  stroke={GRIGLIA}
                  interval="preserveStartEnd"
                  minTickGap={24}
                />
                <YAxis tick={{ fontSize: 10, fill: ASSE }} stroke={GRIGLIA} width={40} allowDecimals={false} />
                <Tooltip content={<TooltipGrafico />} />
                {/* Legenda sempre presente con due serie: l'identità non può
                    essere affidata al solo colore (separazione tritan 6,7). */}
                <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" iconSize={8} />
                <Bar dataKey="ingested" name="Entrate" fill={SERIE_INGRESSO} radius={[4, 4, 0, 0]} maxBarSize={14} />
                <Bar dataKey="approved" name="Approvate" fill={SERIE_APPROVATE} radius={[4, 4, 0, 0]} maxBarSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ marginTop: "var(--space-2)", display: "grid", gap: "var(--space-1)" }}>
            {volume.by_status.map((c) => (
              <div key={c.key} className="text-xs" style={{ display: "flex", gap: "var(--space-2)" }}>
                <span className="truncate">{c.label || statusMeta(c.key).label}</span>
                <span style={{ marginLeft: "auto", fontWeight: 600 }}>
                  <Numero valore={c.value} drill={c.drill} />
                </span>
              </div>
            ))}
          </div>
        </Riquadro>

        <Riquadro titolo="Tempo di ciclo" nota="dall'arrivo della email all'approvazione">
          {!cycle_time.available ? (
            <NonDisponibile availability={cycle_time} />
          ) : (
            <div style={{ display: "grid", gap: "var(--space-3)" }}>
              <div style={{ display: "flex", gap: "var(--space-5)", flexWrap: "wrap" }}>
                <div>
                  <div className="text-lg" style={{ fontWeight: 700 }}>{durata(cycle_time.p50_seconds)}</div>
                  <div className="text-muted text-xs">metà delle pratiche entro (p50)</div>
                </div>
                <div>
                  <div className="text-lg" style={{ fontWeight: 700 }}>{durata(cycle_time.p90_seconds)}</div>
                  <div
                    className="text-muted text-xs"
                    title="È il tempo che il cliente percepisce come «quanto ci mettete»"
                  >
                    nove su dieci entro (p90)
                  </div>
                </div>
              </div>
              <p className="text-muted text-xs" style={{ margin: 0 }}>
                Percentili e non medie: su {nf.format(cycle_time.sample)} pratiche la media sarebbe
                dominata dalle poche rimaste ferme a lungo, e non descriverebbe nessun caso reale.
              </p>
            </div>
          )}
        </Riquadro>
      </div>

      {/* ── 3. adesso — solo se ha qualcosa da dire ──
          Misurato il 2026-07-29: fra le RDI classiche "da lavorare" non e' mai
          stato diverso da zero (gli operatori lavorano in tempo reale), mentre sul
          DDT lo stesso blocco riporta 86 da lavorare, 92 piantate e 7 messaggi non
          prelevati. Tenerlo sempre visibile significherebbe mostrare a Casadei una
          fila di zeri su RDI: un riquadro sempre vuoto e' un riquadro che nessuno
          guarda piu', e quando poi avra' qualcosa da dire nessuno lo leggera'.
          Quindi ogni colonna compare solo se non e' vuota, e se nessuna lo e' il
          riquadro si riduce a una riga. */}
      {(() => {
        const codaDaMostrare = data.output_queue.available && data.output_queue.not_picked_up > 0;
        const qualcosa =
          backlog.open_total > 0 ||
          backlog.broken.length > 0 ||
          backlog.stale_locks > 0 ||
          codaDaMostrare;
        return (
          <Riquadro
            titolo="Adesso"
            nota={`fotografia istantanea, non del periodo · ${quandoRelativo(backlog.as_of)}`}
          >
            {!qualcosa ? (
              <p className="text-sm" style={{ margin: 0, color: "var(--indaco-success)" }}>
                <span
                  className="material-symbols-outlined"
                  style={{ fontSize: 16, verticalAlign: "-3px", marginRight: "var(--space-1)" }}
                >
                  check_circle
                </span>
                Niente da lavorare, niente bloccato, coda output vuota.
              </p>
            ) : (
              <div
                style={{
                  display: "grid",
                  gap: "var(--space-3)",
                  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                }}
              >
                {backlog.open_total > 0 && (
                  <div>
                    <div className="text-xs text-muted">Da lavorare</div>
                    <div style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                      {nf.format(backlog.open_total)}
                    </div>
                    {/* Nessun colore sulle fasce: la lunghezza porta il valore e
                        l'etichetta l'identita', quindi un tono unico basta. */}
                    <ul style={{ listStyle: "none", margin: "var(--space-2) 0 0", padding: 0, display: "grid", gap: 3 }}>
                      {backlog.age_buckets.map((b) => (
                        <li key={b.key} className="text-xs" style={{ display: "flex", gap: "var(--space-2)", alignItems: "center" }}>
                          <span className="text-muted truncate" style={{ minWidth: 92 }}>{b.label}</span>
                          <span
                            style={{
                              flex: 1,
                              height: 6,
                              borderRadius: "var(--border-radius-sm)",
                              background: "var(--indaco-gray-100)",
                              overflow: "hidden",
                              minWidth: 0,
                            }}
                          >
                            <span
                              style={{
                                display: "block",
                                width: `${(b.value / Math.max(1, backlog.open_total)) * 100}%`,
                                height: "100%",
                                borderRadius: "var(--border-radius-sm)",
                                background: SERIE_INGRESSO,
                              }}
                            />
                          </span>
                          <span style={{ fontWeight: 600, minWidth: 28, textAlign: "right" }}>{nf.format(b.value)}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="text-muted text-xs" style={{ marginTop: "var(--space-1)" }}>
                      la più vecchia è arrivata {quandoRelativo(backlog.oldest_arrived_at)}
                    </div>
                  </div>
                )}

                {(backlog.broken.length > 0 || backlog.stale_locks > 0) && (
                  <div>
                    <div className="text-xs text-muted">Piantate</div>
                    <ul style={{ listStyle: "none", margin: "var(--space-1) 0 0", padding: 0, display: "grid", gap: 3 }}>
                      {backlog.broken.map((c) => (
                        <li
                          key={c.key}
                          className="text-xs"
                          style={{ display: "flex", gap: "var(--space-2)", maxWidth: 220 }}
                        >
                          <span className="truncate">{statusMeta(c.key).label}</span>
                          <span style={{ marginLeft: "auto", fontWeight: 600, flexShrink: 0 }}>
                            <Numero valore={c.value} drill={c.drill} />
                          </span>
                        </li>
                      ))}
                      {backlog.stale_locks > 0 && (
                        <li
                          className="text-xs"
                          style={{ display: "flex", gap: "var(--space-2)", maxWidth: 220 }}
                        >
                          <span
                            className="truncate"
                            title={`In carico a un operatore da oltre ${backlog.stale_lock_threshold_hours} ore: di fatto abbandonate`}
                          >
                            Lock abbandonati
                          </span>
                          <span style={{ marginLeft: "auto", fontWeight: 600, color: "var(--indaco-warning)" }}>
                            {nf.format(backlog.stale_locks)}
                          </span>
                        </li>
                      )}
                    </ul>
                  </div>
                )}

                {codaDaMostrare && (
                  <div>
                    <div className="text-xs text-muted">Coda output non prelevata</div>
                    <div style={{ fontSize: "1.5rem", fontWeight: 700 }}>
                      {nf.format(data.output_queue.not_picked_up)}
                    </div>
                    <div className="text-muted text-xs">
                      il più vecchio inviato {quandoRelativo(data.output_queue.oldest_sent_at)}
                    </div>
                  </div>
                )}
              </div>
            )}
          </Riquadro>
        );
      })()}

      {/* ── 4. scarti: dove si capisce quanto e come il sistema sbaglia ── */}
      <Riquadro
        titolo="Scarti"
        nota={scartiDaMostrare ? `${nf.format(discards.total.current)} nel periodo` : undefined}
        azione={scartiDaMostrare ? <Delta comparison={discards.total} /> : undefined}
      >
        {!discards.available ? (
          <NonDisponibile availability={discards} />
        ) : !scartiDaMostrare ? (
          /* Zero scarti e nessuna ripartizione calcolabile: è il caso del DDT, dove
             il riquadro mostrava tre messaggi "non disponibile" attorno a uno zero.
             Tre spiegazioni di ciò che manca non dicono nulla di ciò che è
             successo — e un riquadro che non dice nulla è un riquadro che poi
             nessuno legge quando avrà qualcosa da dire. */
          <p className="text-sm" style={{ margin: 0, color: "var(--indaco-success)" }}>
            <span
              className="material-symbols-outlined"
              style={{ fontSize: 16, verticalAlign: "-3px", marginRight: "var(--space-1)" }}
              aria-hidden
            >
              check_circle
            </span>
            Nessuno scarto nel periodo.
          </p>
        ) : (
          <div style={{ display: "grid", gap: "var(--space-4)", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
            <div style={{ minWidth: 0 }}>
              <div className="text-xs text-muted" style={{ marginBottom: "var(--space-2)" }}>
                Perché, secondo quello che l'operatore ha scritto
              </div>
              {discards.families_availability.available ? (
                <>
                  <Classifica
                    voci={discards.families.map((f) => ({
                      key: f.key,
                      label: f.label || f.key,
                      value: f.value,
                      drill: f.drill,
                    }))}
                    totale={discards.total.current || undefined}
                    evidenzia={(k) => k === "errore_sistema"}
                  />
                  {discards.unclassified_notes > 0 && (
                    <p className="text-muted text-xs" style={{ marginTop: "var(--space-2)", marginBottom: 0 }}>
                      {nf.format(discards.unclassified_notes)} note non ancora classificate, contate a
                      parte per non far sembrare la classifica più completa di quanto sia.
                    </p>
                  )}
                </>
              ) : (
                <NonDisponibile availability={discards.families_availability} />
              )}
            </div>

            <div style={{ minWidth: 0 }}>
              <div
                className="text-xs text-muted"
                style={{ marginBottom: "var(--space-2)" }}
                title="Le categorie che l'operatore ha selezionato nell'interfaccia. Affiancate alle famiglie per rendere visibile lo scarto fra le due: dove divergono, la nota è più affidabile della categoria."
              >
                Categoria selezionata dall'operatore
              </div>
              {discards.by_operator_availability.available ? (
                <Classifica
                  voci={discards.by_operator.map((c) => ({
                    key: c.key,
                    label: c.label || c.key,
                    value: c.value,
                    drill: c.drill,
                  }))}
                />
              ) : (
                <NonDisponibile availability={discards.by_operator_availability} />
              )}

              {discards.automatic_availability.available && discards.automatic.length > 0 && (
                <>
                  <div className="text-xs text-muted" style={{ margin: "var(--space-3) 0 var(--space-2)" }}>
                    Scartate dalla pipeline, senza operatore
                  </div>
                  <Classifica
                    voci={discards.automatic.map((c) => ({
                      key: c.key,
                      label: c.label || c.key,
                      value: c.value,
                      drill: c.drill,
                    }))}
                  />
                </>
              )}
            </div>
          </div>
        )}
      </Riquadro>

      {/* ── 5. dove si consuma il lavoro manuale ── */}
      <Riquadro
        titolo="Campi corretti a mano"
        nota={
          hotspots.available
            ? `su ${nf.format(hotspots.approved_in_period)} approvate nel periodo`
            : undefined
        }
      >
        {!hotspots.available ? (
          <NonDisponibile availability={hotspots} />
        ) : (
          <div style={{ display: "grid", gap: "var(--space-4)", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))" }}>
            <Classifica
              voci={hotspots.fields.map((f) => ({
                key: f.field,
                label: f.label,
                value: f.practices,
                drill: f.drill,
              }))}
              totale={hotspots.approved_in_period || undefined}
            />
            {hotspots.categories.length > 0 && (
              <div style={{ minWidth: 0 }}>
                <div className="text-xs text-muted" style={{ marginBottom: "var(--space-2)" }}>
                  Per categoria di correzione
                </div>
                <Classifica
                  voci={hotspots.categories.map((c) => ({
                    key: c.key,
                    label: c.label || c.key,
                    value: c.value,
                    drill: c.drill,
                  }))}
                />
              </div>
            )}
          </div>
        )}
      </Riquadro>

      {/* ── 6. su quali entità il sistema fa più fatica ──
          Complementare al riquadro sopra, non una sua variante: i campi dicono
          *cosa* si corregge, le entità dicono *dove*. Le due letture portano a
          interventi diversi — un campo sempre sbagliato è un problema di
          estrazione, un fornitore sempre sbagliato è un problema di anagrafica del
          cliente, e solo il secondo si risolve parlando con Casadei. */}
      {data.entities.map((dim) => (
        <Riquadro
          key={dim.key}
          titolo={`Per ${dim.label.toLowerCase()}`}
          nota={
            dim.available
              ? `${nf.format(dim.practices_in_period)} pratiche nel periodo · a destra quante hanno richiesto una correzione`
              : undefined
          }
        >
          <RipartizioneEntita dim={dim} />
        </Riquadro>
      ))}
    </div>
  );
}
